import { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Card,
  Row,
  Col,
  Button,
  Space,
  Typography,
  Alert,
  message,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import {
  CalculatorOutlined,
  SaveOutlined,
  SendOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { dimensionsService } from '../services/dimensionsService';
import { cogsService } from '../services/cogsService';
import { formatCurrency } from '../utils/formatters';
import {
  FLOW_OPTIONS,
  FOREIGN_LOCAL_MERCHANT_OPTIONS,
  FUND_SOURCE_OPTIONS,
  INSTALLMENT_INTEREST_OPTIONS,
  INSTALLMENT_TERM_OPTIONS,
  IS_ON_US_OPTIONS,
  PROCESSOR_OPTIONS,
  CARD_TYPE_OPTIONS,
  CARD_BRAND_OPTIONS,
  CARD_LOCAL_FOREIGN_OPTIONS,
  CARD_TIER_OPTIONS,
  INDUSTRY_OPTIONS,
  PAYOUT_CONNECTOR_OPTIONS,
  PAYOUT_RAIL_OPTIONS,
  ACQUIRER_OPTIONS,
  EWALLET_INDUSTRY_OPTIONS,
  OTC_INDUSTRY_OPTIONS,
  CARDS_INDUSTRY_OPTIONS,
  FX_LIQUIDITY_PARTNER_OPTIONS,
  FX_BASE_RATE_SOURCE_OPTIONS,
  FX_VALUE_DATE_CONVENTION_OPTIONS,
  CURRENCY_ISO_4217_OPTIONS,
} from '../constants/formEnums';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Product to Channel auto-selection mapping
// When these products are selected, the corresponding channel is auto-selected and locked
const PRODUCT_CHANNEL_MAPPING = {
  6: 4047,  // Payouts → PAYOUT
  8: 70,    // Remittance → REMITTANCE
};

// Component for managing multiple MIDs
const MIDsInput = ({ value = [], onChange }) => {
  const handleAdd = () => {
    const newMids = [...value, { mid: '', subMid: '' }];
    onChange(newMids);
  };

  const handleRemove = (index) => {
    const newMids = value.filter((_, i) => i !== index);
    onChange(newMids);
  };

  const handleChange = (index, field, fieldValue) => {
    const newMids = [...value];
    newMids[index] = { ...newMids[index], [field]: fieldValue };
    onChange(newMids);
  };

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
        💡 Hint: leave empty to make this the default (catch-all) rule for any MID — saved as "default"
      </div>
      {value.map((mid, index) => (
        <Row key={index} gutter={[8, 8]} style={{ marginBottom: '8px' }}>
          <Col flex="1">
            <Input
              placeholder="Enter MID (optional)"
              value={mid.mid}
              onChange={(e) => handleChange(index, 'mid', e.target.value)}
            />
          </Col>
          <Col flex="1">
            <Input
              placeholder="Enter Sub MID (optional)"
              value={mid.subMid}
              onChange={(e) => handleChange(index, 'subMid', e.target.value)}
            />
          </Col>
          <Col>
            <Tooltip title="Remove MID">
              <Button
                type="text"
                icon={<MinusCircleOutlined />}
                onClick={() => handleRemove(index)}
                disabled={value.length === 1}
                danger
              />
            </Tooltip>
          </Col>
        </Row>
      ))}
      <Button
        type="dashed"
        onClick={handleAdd}
        icon={<PlusOutlined />}
        style={{ width: '100%' }}
      >
        Add MID
      </Button>
    </div>
  );
};

// Component for managing multiple MCCs (mirrors MIDsInput; single 4-digit value per row)
const MCCsInput = ({ value = [], onChange }) => {
  const handleAdd = () => {
    onChange([...value, '']);
  };

  const handleRemove = (index) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleChange = (index, fieldValue) => {
    const newMccs = [...value];
    newMccs[index] = fieldValue;
    onChange(newMccs);
  };

  return (
    <div>
      <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
        💡 Hint: leave empty to make this the default (catch-all) rule for all MCCs — saved as MCC 0000
      </div>
      {value.map((mcc, index) => (
        <Row key={index} gutter={[8, 8]} style={{ marginBottom: '8px' }}>
          <Col flex="1">
            <Input
              placeholder="Enter MCC (4 digits, optional)"
              maxLength={4}
              value={mcc}
              onChange={(e) => handleChange(index, e.target.value)}
            />
          </Col>
          <Col>
            <Tooltip title="Remove MCC">
              <Button
                type="text"
                icon={<MinusCircleOutlined />}
                onClick={() => handleRemove(index)}
                danger
              />
            </Tooltip>
          </Col>
        </Row>
      ))}
      <Button
        type="dashed"
        onClick={handleAdd}
        icon={<PlusOutlined />}
        style={{ width: '100%' }}
      >
        Add MCC
      </Button>
    </div>
  );
};

const COGSRuleForm = ({
  initialData = null,
  mode = 'create', // 'create', 'edit', or 'edit-as-new'
  onSave,
  onCancel,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [formLoading, setFormLoading] = useState(false);
  const [dimensions, setDimensions] = useState({});
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [feePreview, setFeePreview] = useState(null);
  const [previewAmount, setPreviewAmount] = useState(100000);

  // Check if channel should be locked (auto-selected and disabled) based on product
  const isChannelLocked = selectedProduct?.id in PRODUCT_CHANNEL_MAPPING;

  // Product-specific mandatory fields mapping
  // Note: MID is now optional for all products and can be empty
  const PRODUCT_MANDATORY_FIELDS = {
    'Virtual Account': [],
    'Direct Debit': ['industry', 'flow', 'fundSource'],
    'E-Wallet': ['industry', 'flow', 'foreignLocalMerchant', 'fundSource', 'installmentTerm'],
    'OTC': ['industry'],
    'Paylater': ['installmentInterest', 'installmentTerm'],
    'Payouts': ['payoutRailName', 'payoutConnectorName'],
    'Remittance': ['payoutRailName', 'payoutConnectorName'],
    'Bill Payment': ['payoutRailName', 'payoutConnectorName'],
    'QR': ['acquirerName', 'onUs'],
    'Cards': ['installmentTerm', 'acquirerName', 'onUs', 'processor', 'cardType', 'cardBrandName', 'cardTier', 'cardLocalForeign'],
    'FOREX': ['sourceCurrencyCode', 'destinationCurrencyCode', 'liquidityPartner', 'baseRateSource', 'bidSpreadBps', 'askSpreadBps', 'valueDateConvention'],
  };

  // Load dimension data
  useEffect(() => {
    const loadDimensions = async () => {
      try {
        const dimensionData = await dimensionsService.getAllDimensions();
        setDimensions(dimensionData);
      } catch (error) {
        message.error('Failed to load form options');
      }
    };

    loadDimensions();
  }, []);

  // Handle product selection change
  const handleProductChange = (productId) => {
    const product = dimensions.products?.find(p => p.id === productId);
    setSelectedProduct(product);

    // Clear product-specific fields when product changes
    if (product) {
      const fieldsToReset = {};
      Object.values(PRODUCT_MANDATORY_FIELDS).flat().forEach(field => {
        if (field === 'mid' || field === 'subMid') {
          // Reset MIDs to empty array
          fieldsToReset.mids = [{ mid: '', subMid: '' }];
        } else {
          fieldsToReset[field] = undefined;
        }
      });

      // MCCs aren't in the mandatory-fields loop, so reset them explicitly (like MIDs)
      fieldsToReset.mccs = [''];

      // Auto-select channel if product has a mapped channel (Payouts/Remittance)
      if (productId in PRODUCT_CHANNEL_MAPPING) {
        fieldsToReset.channelId = PRODUCT_CHANNEL_MAPPING[productId];
      } else {
        // Clear channel when switching from a locked product to a non-locked one
        fieldsToReset.channelId = undefined;
      }

      // FOREX-specific: force VAT to NON_VAT and set valueDateConvention
      if (product.name === 'FOREX') {
        fieldsToReset.includeVAT = 'NON_VAT';
        fieldsToReset.vatRate = null;
        fieldsToReset.valueDateConvention = 'T+0';
      }

      form.setFieldsValue(fieldsToReset);
    }
  };

  // Check if a field is required for the selected product
  const isFieldRequired = (fieldName) => {
    if (!selectedProduct) return false;
    const mandatoryFields = PRODUCT_MANDATORY_FIELDS[selectedProduct.name] || [];
    return mandatoryFields.includes(fieldName);
  };

  // Check if a field should be visible for the selected product
  const isFieldVisible = (fieldName) => {
    if (!selectedProduct) return false;

    // MID is now optional but visible for most products
    if (fieldName === 'mid') {
      const productsWithMid = ['Virtual Account', 'Direct Debit', 'E-Wallet', 'OTC', 'Paylater', 'QR', 'Cards'];
      return productsWithMid.includes(selectedProduct.name);
    }

    // MCC is optional but visible for Cards
    if (fieldName === 'mccs') {
      return selectedProduct.name === 'Cards';
    }

    const mandatoryFields = PRODUCT_MANDATORY_FIELDS[selectedProduct.name] || [];
    return mandatoryFields.includes(fieldName);
  };

  // Get industry options based on selected product
  const getIndustryOptions = () => {
    if (!selectedProduct) return INDUSTRY_OPTIONS;

    switch (selectedProduct.name) {
      case 'E-Wallet':
        return EWALLET_INDUSTRY_OPTIONS;
      case 'OTC':
        return OTC_INDUSTRY_OPTIONS;
      case 'Cards':
        return CARDS_INDUSTRY_OPTIONS;
      default:
        return INDUSTRY_OPTIONS;
    }
  };

  // Set initial form values
  useEffect(() => {
    if (initialData) {
      // Convert MIDs from backend format to frontend format
      let midsData = [];
      if (initialData.mids && Array.isArray(initialData.mids)) {
        // New format: array of MID objects; the 'default' sentinel row is shown as an empty field
        midsData = initialData.mids
          .filter(midObj => String(midObj.mid || '').trim().toLowerCase() !== 'default')
          .map(midObj => ({
            mid: midObj.mid || '',
            subMid: midObj.subMid || ''
          }));
      } else if (initialData.mid) {
        // Legacy format: single MID and subMid fields
        midsData = [{
          mid: initialData.mid,
          subMid: initialData.subMid || ''
        }];
      }

      // Ensure at least one empty MID entry if none exist
      if (midsData.length === 0) {
        midsData = [{ mid: '', subMid: '' }];
      }

      // Convert MCCs to frontend rows; the '0000' sentinel row is shown as an empty field
      let mccsData = [];
      if (initialData.mccs && Array.isArray(initialData.mccs)) {
        mccsData = initialData.mccs.map(mccObj => mccObj.mcc).filter(mcc => String(mcc).trim() !== '0000');
      } else if (initialData.mcc) {
        mccsData = [initialData.mcc];
      }

      // Ensure at least one empty MCC entry if none exist
      if (mccsData.length === 0) {
        mccsData = [''];
      }

      // Enforce correct channel for products with locked channels (Payouts/Remittance)
      let channelId = initialData.channelId;
      if (initialData.productId in PRODUCT_CHANNEL_MAPPING) {
        channelId = PRODUCT_CHANNEL_MAPPING[initialData.productId];
      }

      form.setFieldsValue({
        ...initialData,
        channelId,
        mids: midsData,
        mccs: mccsData,
        effectiveStartDate: initialData.effectiveStartDate ? dayjs(initialData.effectiveStartDate) : null,
        vatRate: initialData.vatRate != null ? parseFloat((initialData.vatRate * 100).toFixed(4)) : null,
        // Reverse map backend field names to frontend form field names
        industry: initialData.industryName || initialData.industry,
        acquirer: initialData.acquirerName || initialData.acquirer,
        cardBrand: initialData.cardBrandName || initialData.cardBrand,
        payoutConnector: initialData.payoutConnectorName || initialData.payoutConnector,
        payoutRail: initialData.payoutRailName || initialData.payoutRail,
      });

      // Set selected scheme for conditional fields
      if (initialData.schemeId) {
        const scheme = dimensions.schemes?.find(s => s.id === initialData.schemeId);
        setSelectedScheme(scheme);
      }

      // Set selected currency
      if (initialData.feeCurrencyCode) {
        setSelectedCurrency(initialData.feeCurrencyCode);
      }

      // Set selected product for conditional fields
      if (initialData.productId) {
        const product = dimensions.products?.find(p => p.id === initialData.productId);
        setSelectedProduct(product);
      }
    } else {
      // Initialize with empty MID entry for new forms
      form.setFieldsValue({
        mids: [{ mid: '', subMid: '' }],
        mccs: ['']
      });
    }
  }, [initialData, dimensions, form]);

  // Handle scheme change
  const handleSchemeChange = (schemeId) => {
    const scheme = dimensions.schemes?.find(s => s.id === schemeId);
    const previousScheme = selectedScheme;
    setSelectedScheme(scheme);

    if (previousScheme && previousScheme.id !== schemeId) {
      form.setFieldsValue({
        flatFee: undefined,
        percentageRate: undefined,
        minimumFee: undefined,
        maximumFee: undefined,
        revenueShareKeptByPartner: undefined,
        tieredPricing: {
          tierAllOrStack: undefined,
          tierCountModel: undefined,
          tier1End: undefined,
          tier1FixedFee: undefined,
          tier1MDR: undefined,
          tier2End: undefined,
          tier2FixedFee: undefined,
          tier2MDR: undefined,
          tier3End: undefined,
          tier3FixedFee: undefined,
          tier3MDR: undefined,
          tier4FixedFee: undefined,
          tier4MDR: undefined,
        },
      });
    }
  };

  // Handle currency change
  const handleCurrencyChange = (currencyCode) => {
    setSelectedCurrency(currencyCode);
  };

  // Render pricing fields based on selected scheme
  const renderPricingFields = () => {
    if (!selectedScheme) return null;

    const currency = selectedCurrency;

    switch (selectedScheme.scheme) {
      case 'fixed':
        return (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="flatFee" label="Flat Fee" rules={[{ required: true, message: 'Please enter flat fee' }]}>
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={4}
                  addonAfter={currency}
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="minimumFee" label="Minimum Fee">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={4}
                  addonAfter={currency}
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="maximumFee" label="Maximum Fee">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={4}
                  addonAfter={currency}
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
          </Row>
        );

      case 'mdr':
        return (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="percentageRate" label="Percentage Rate (%)" rules={[{ required: true, message: 'Please enter percentage rate' }]}>
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  max={100}
                  precision={6}
                  addonAfter="%"
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="minimumFee" label="Minimum Fee">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={4}
                  addonAfter={currency}
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="maximumFee" label="Maximum Fee">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={4}
                  addonAfter={currency}
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
          </Row>
        );

      case 'mdr-fixed':
        return (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="flatFee" label="Flat Fee" rules={[{ required: true, message: 'Please enter flat fee' }]}>
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={4}
                  addonAfter={currency}
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="percentageRate" label="Percentage Rate (%)" rules={[{ required: true, message: 'Please enter percentage rate' }]}>
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  max={100}
                  precision={6}
                  addonAfter="%"
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="minimumFee" label="Minimum Fee">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={4}
                  addonAfter={currency}
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="maximumFee" label="Maximum Fee">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={4}
                  addonAfter={currency}
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
          </Row>
        );

      case 'revenue-sharing':
        return (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="percentageRate" label="Percentage Rate (%)" rules={[{ required: true, message: 'Please enter percentage rate' }]}>
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  max={100}
                  precision={6}
                  addonAfter="%"
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="revenueShareKeptByPartner" label="Revenue Share Kept by Partner (%)" rules={[{ required: true, message: 'Please enter revenue share percentage' }]}>
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  max={100}
                  precision={6}
                  addonAfter="%"
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="minimumFee" label="Minimum Fee">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={4}
                  addonAfter={currency}
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="maximumFee" label="Maximum Fee">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={4}
                  addonAfter={currency}
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
          </Row>
        );

      case 'special':
        return (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="flatFee" label="Flat Fee">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={4}
                  addonAfter={currency}
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="percentageRate" label="Percentage Rate (%)">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  max={100}
                  precision={6}
                  addonAfter="%"
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="minimumFee" label="Minimum Fee">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={4}
                  addonAfter={currency}
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="maximumFee" label="Maximum Fee">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={4}
                  addonAfter={currency}
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
          </Row>
        );

      default:
        return (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="flatFee" label="Flat Fee">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={4}
                  addonAfter={currency}
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="percentageRate" label="Percentage Rate (%)">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  max={100}
                  precision={6}
                  addonAfter="%"
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="minimumFee" label="Minimum Fee">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={4}
                  addonAfter={currency}
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="maximumFee" label="Maximum Fee">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={4}
                  addonAfter={currency}
                  onChange={calculatePreview}
                />
              </Form.Item>
            </Col>
          </Row>
        );
    }
  };

  // Calculate fee preview
  const calculatePreview = async () => {
    try {
      const formValues = form.getFieldsValue();
      const fee = await cogsService.calculateFee(formValues, previewAmount);
      setFeePreview(fee);
    } catch (error) {
      // Error calculating fee preview handled by UI state
    }
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    setFormLoading(true);
    try {
      // Format MIDs data - filter out empty MIDs
      const formattedMids = values.mids ? values.mids.filter(mid => mid.mid && mid.mid.trim() !== '') : [];

      // Format MCCs data - filter out empty rows
      const formattedMccs = values.mccs ? values.mccs.map(m => String(m).trim()).filter(m => m !== '') : [];

      // Validate form data
      const validation = cogsService.validateRule({ ...values, mids: formattedMids });
      if (!validation.isValid) {
        message.error('Please fix validation errors');
        validation.errors.forEach(error => message.error(error));
        return;
      }

      // Format dates and MIDs
      const formattedValues = {
        ...values,
        mids: formattedMids,
        mccs: formattedMccs,
        effectiveStartDate: values.effectiveStartDate?.toISOString(),
        vatRate: values.includeVAT === 'TRUE' && values.vatRate != null ? values.vatRate / 100 : null,
      };

      if (onSave) {
        await onSave(formattedValues);
      }
    } catch (error) {
      message.error('Failed to save COGS rule');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle submit for approval
  const handleSubmitForApproval = async () => {
    setFormLoading(true);
    try {
      const values = await form.validateFields();

      // Format MIDs data - filter out empty MIDs
      const formattedMids = values.mids ? values.mids.filter(mid => mid.mid && mid.mid.trim() !== '') : [];

      // Format MCCs data - filter out empty rows
      const formattedMccs = values.mccs ? values.mccs.map(m => String(m).trim()).filter(m => m !== '') : [];

      // Validate product-specific fields
      if (selectedProduct) {
        const mandatoryFields = PRODUCT_MANDATORY_FIELDS[selectedProduct.name] || [];
        const missingFields = [];

        for (const field of mandatoryFields) {
          if (field === 'mid') {
            // Check for MIDs in the new array format
            if (formattedMids.length === 0) {
              missingFields.push('mids (at least one MID is required)');
            }
          } else if (field === 'subMid') {
            // SubMid is now part of the mids array, so we don't check it separately
            continue;
          } else {
            // Check other fields normally
            if (!values[field] || values[field] === '' || values[field] === null) {
              missingFields.push(field);
            }
          }
        }

        if (missingFields.length > 0) {
          message.error(`Missing mandatory fields for ${selectedProduct.name}: ${missingFields.join(', ')}`);
          setFormLoading(false);
          return;
        }
      }

      // Format dates and MIDs
      const formattedValues = {
        ...values,
        mids: formattedMids,
        mccs: formattedMccs,
        effectiveStartDate: values.effectiveStartDate?.toISOString(),
        vatRate: values.includeVAT === 'TRUE' && values.vatRate != null ? values.vatRate / 100 : null,
      };

      if (mode === 'create') {
        // Use the new create and submit endpoint
        await cogsService.createAndSubmitRule(formattedValues);
        message.success('COGS rule created and submitted for approval');
      } else if (mode === 'edit-as-new' && initialData?.id) {
        // Create new version from existing rule (edit-as-new)
        await cogsService.createNewVersionFromExisting(initialData.id, formattedValues);
        message.success('New rule version created and submitted for approval');
      } else if (initialData?.id) {
        // Update existing rule and submit for approval (legacy edit mode)
        await cogsService.updateRule(initialData.id, formattedValues);
        await cogsService.submitForApproval(initialData.id);
        message.success('COGS rule updated and submitted for approval');
      }

      if (onSave) {
        onSave(formattedValues, { skipApiCall: true });
      }
    } catch (error) {
      message.error('Failed to submit for approval');
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={2}>
          {mode === 'create' ? 'Create New COGS Rule' :
           mode === 'edit-as-new' ? 'Create New Version of COGS Rule' :
           'Edit COGS Rule'}
        </Title>
        
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            includeVAT: 'FALSE',
            costRefundedOnRefund: 'FALSE',
            costRefundedOnVoid: 'FALSE',
            costRefundedOnChargeback: 'FALSE',
          }}
        >
          {/* Basic Information */}
          <Card size="small" title="Basic Information" style={{ marginBottom: '24px' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="entityId"
                  label="Transaction Entity"
                  rules={[{ required: true, message: 'Please select an entity' }]}
                >
                  <Select
                    placeholder="Select entity"
                    showSearch
                    optionFilterProp="children"
                  >
                    {dimensions.entities?.map(entity => (
                      <Option key={entity.id} value={entity.id}>
                        {entity.name} ({entity.country})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="productId"
                  label="Product"
                  rules={[{ required: true, message: 'Please select a product' }]}
                >
                  <Select
                    placeholder="Select product"
                    showSearch
                    optionFilterProp="children"
                    onChange={handleProductChange}
                  >
                    {dimensions.products?.map(product => (
                      <Option key={product.id} value={product.id}>
                        {product.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="schemeId"
                  label="Pricing Scheme"
                  rules={[{ required: true, message: 'Please select a pricing scheme' }]}
                >
                  <Select
                    placeholder="Select scheme"
                    showSearch
                    optionFilterProp="children"
                    onChange={handleSchemeChange}
                    dropdownStyle={{ minWidth: '900px' }}
                  >
                    {dimensions.schemes?.map(scheme => (
                      <Option key={scheme.id} value={scheme.id}>
                        {scheme.scheme} - {scheme.description}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="feeCurrencyCode"
                  label="Currency"
                  rules={[{ required: true, message: 'Please select a currency' }]}
                >
                  <Select
                    placeholder="Select currency"
                    onChange={handleCurrencyChange}
                  >
                    {dimensions.currencies?.map(currency => (
                      <Option key={currency.code} value={currency.code}>
                        {currency.code}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="channelId"
                  label="Channel"
                  rules={[{ required: true, message: 'Please select a channel' }]}
                  tooltip={isChannelLocked ? `Channel is auto-selected for ${selectedProduct?.name}` : undefined}
                >
                  <Select
                    placeholder="Select channel"
                    showSearch
                    optionFilterProp="children"
                    disabled={isChannelLocked}
                  >
                    {dimensions.channels?.map(channel => (
                      <Option key={channel.id} value={channel.id}>
                        {channel.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="businessId" label="Business ID (Optional)">
                  <Input placeholder="Enter business ID" />
                </Form.Item>
              </Col>

              {/* Industry - conditional based on product */}
              {(selectedProduct && isFieldVisible('industry')) && (
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="industry"
                    label="Industry"
                    rules={[
                      { required: isFieldRequired('industry'), message: 'Industry is required for this product' }
                    ]}
                  >
                    <Select placeholder="Select Industry" showSearch optionFilterProp="children">
                      {getIndustryOptions().map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Card>

          {/* Product-Specific Fields */}
          {selectedProduct && (
            <Card
              size="small"
              title={`${selectedProduct.name} Specific Fields`}
              style={{ marginBottom: '24px' }}
            >
              <Alert
                message={`Required fields for ${selectedProduct.name}`}
                description={`Please fill in all mandatory fields for ${selectedProduct.name} product.`}
                type="info"
                showIcon
                style={{ marginBottom: '16px' }}
              />
              <Row gutter={[16, 16]}>
                {/* MIDs field - now optional for all products */}
                {isFieldVisible('mid') && (
                  <Col xs={24}>
                    <Form.Item
                      name="mids"
                      label="MIDs (Merchant IDs) - Optional"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (!value || value.length === 0) {
                              // MIDs are optional, so empty is allowed
                              return Promise.resolve();
                            }

                            // Check for duplicate MIDs (only for non-empty MIDs)
                            const nonEmptyMids = value
                              .map(midData => midData.mid)
                              .filter(mid => mid && mid.trim() !== '');

                            const uniqueMids = [...new Set(nonEmptyMids)];
                            if (nonEmptyMids.length !== uniqueMids.length) {
                              return Promise.reject(new Error('Duplicate MIDs are not allowed'));
                            }

                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <MIDsInput required={false} />
                    </Form.Item>
                  </Col>
                )}

                {/* Payout Connector - required for Payouts and Remittance */}
                {isFieldVisible('payoutConnectorName') && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="payoutConnectorName"
                      label="Payout Connector"
                      rules={[
                        { required: isFieldRequired('payoutConnectorName'), message: 'Payout Connector is required for this product' }
                      ]}
                    >
                      <Select placeholder="Select Payout Connector">
                        {PAYOUT_CONNECTOR_OPTIONS.map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {/* Payout Rail - required for Payouts and Remittance */}
                {isFieldVisible('payoutRailName') && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="payoutRailName"
                      label="Payout Rail"
                      rules={[
                        { required: isFieldRequired('payoutRailName'), message: 'Payout Rail is required for this product' }
                      ]}
                    >
                      <Select placeholder="Select Payout Rail">
                        {PAYOUT_RAIL_OPTIONS.map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {/* Flow - required for E-Wallet */}
                {isFieldVisible('flow') && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="flow"
                      label="Flow"
                      rules={[
                        { required: isFieldRequired('flow'), message: 'Flow is required for this product' }
                      ]}
                    >
                      <Select placeholder="Select Flow">
                        {FLOW_OPTIONS.map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {/* Foreign/Local Merchant - required for E-Wallet */}
                {isFieldVisible('foreignLocalMerchant') && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="foreignLocalMerchant"
                      label="Foreign/Local Merchant"
                      rules={[
                        { required: isFieldRequired('foreignLocalMerchant'), message: 'Foreign/Local Merchant is required for this product' }
                      ]}
                    >
                      <Select placeholder="Select type">
                        {FOREIGN_LOCAL_MERCHANT_OPTIONS.map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {/* Fund Source - required for E-Wallet */}
                {isFieldVisible('fundSource') && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="fundSource"
                      label="Fund Source"
                      rules={[
                        { required: isFieldRequired('fundSource'), message: 'Fund Source is required for this product' }
                      ]}
                    >
                      <Select placeholder="Select Fund Source">
                        {FUND_SOURCE_OPTIONS.map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {/* Installment Term - required for E-Wallet, Paylater, Cards */}
                {isFieldVisible('installmentTerm') && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="installmentTerm"
                      label="Installment Term"
                      rules={[
                        { required: isFieldRequired('installmentTerm'), message: 'Installment Term is required for this product' }
                      ]}
                    >
                      <Select placeholder="Select Installment Term">
                        {INSTALLMENT_TERM_OPTIONS.map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {/* Installment Interest - required for Paylater */}
                {isFieldVisible('installmentInterest') && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="installmentInterest"
                      label="Installment Interest"
                      rules={[
                        { required: isFieldRequired('installmentInterest'), message: 'Installment Interest is required for this product' }
                      ]}
                    >
                      <Select placeholder="Select Installment Interest">
                        {INSTALLMENT_INTEREST_OPTIONS.map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {/* Acquirer Name - required for QR and Cards */}
                {isFieldVisible('acquirerName') && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="acquirerName"
                      label="Acquirer"
                      rules={[
                        { required: isFieldRequired('acquirerName'), message: 'Acquirer is required for this product' }
                      ]}
                    >
                      <Select placeholder="Select Acquirer">
                        {ACQUIRER_OPTIONS.map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {/* On Us - required for QR and Cards */}
                {isFieldVisible('onUs') && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="onUs"
                      label="On Us"
                      rules={[
                        { required: isFieldRequired('onUs'), message: 'On Us is required for this product' }
                      ]}
                    >
                      <Select placeholder="Select On Us">
                        {IS_ON_US_OPTIONS.map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {/* Processor - required for Cards */}
                {isFieldVisible('processor') && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="processor"
                      label="Processor"
                      rules={[
                        { required: isFieldRequired('processor'), message: 'Processor is required for this product' }
                      ]}
                    >
                      <Select placeholder="Select Processor">
                        {PROCESSOR_OPTIONS.map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {/* Card Type - required for Cards */}
                {isFieldVisible('cardType') && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="cardType"
                      label="Card Type"
                      rules={[
                        { required: isFieldRequired('cardType'), message: 'Card Type is required for this product' }
                      ]}
                    >
                      <Select placeholder="Select card type">
                        {CARD_TYPE_OPTIONS.map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {/* Card Brand Name - required for Cards */}
                {isFieldVisible('cardBrandName') && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="cardBrandName"
                      label="Card Brand"
                      rules={[
                        { required: isFieldRequired('cardBrandName'), message: 'Card Brand is required for this product' }
                      ]}
                    >
                      <Select placeholder="Select Card Brand">
                        {CARD_BRAND_OPTIONS.map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {/* Card Tier - required for Cards */}
                {isFieldVisible('cardTier') && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="cardTier"
                      label="Card Tier"
                      rules={[
                        { required: isFieldRequired('cardTier'), message: 'Card Tier is required for this product' }
                      ]}
                    >
                      <Select placeholder="Select Card Tier">
                        {CARD_TIER_OPTIONS.map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {/* Card Local/Foreign - required for Cards */}
                {isFieldVisible('cardLocalForeign') && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="cardLocalForeign"
                      label="Card Local/Foreign"
                      rules={[
                        { required: isFieldRequired('cardLocalForeign'), message: 'Card Local/Foreign is required for this product' }
                      ]}
                    >
                      <Select placeholder="Select type">
                        {CARD_LOCAL_FOREIGN_OPTIONS.map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {/* MCCs - optional, shown for Cards; multiple 4-digit codes (same UX as MIDs) */}
                {isFieldVisible('mccs') && (
                  <Col xs={24}>
                    <Form.Item
                      name="mccs"
                      label="MCCs (Merchant Category Codes) - Optional"
                      rules={[
                        {
                          validator: (_, value) => {
                            // Ignore empty rows (optional); validate only the codes the user typed
                            const invalid = (value || [])
                              .map(v => String(v).trim())
                              .filter(v => v !== '' && !/^\d{4}$/.test(v));
                            return invalid.length > 0
                              ? Promise.reject(new Error(`Each MCC must be exactly 4 digits: ${invalid.join(', ')}`))
                              : Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <MCCsInput />
                    </Form.Item>
                  </Col>
                )}

                {/* FOREX-specific fields */}
                {selectedProduct?.name === 'FOREX' && (
                  <>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item
                        name="sourceCurrencyCode"
                        label="Base Currency"
                        rules={[{ required: true, message: 'Base currency is required for FOREX' }]}
                      >
                        <Select placeholder="Select base currency" showSearch optionFilterProp="children">
                          {CURRENCY_ISO_4217_OPTIONS.map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item
                        name="destinationCurrencyCode"
                        label="Quote Currency"
                        rules={[{ required: true, message: 'Quote currency is required for FX' }]}
                      >
                        <Select placeholder="Select quote currency" showSearch optionFilterProp="children">
                          {CURRENCY_ISO_4217_OPTIONS.map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item
                        name="liquidityPartner"
                        label="Liquidity Partner"
                        rules={[{ required: true, message: 'Liquidity partner is required for FX' }]}
                      >
                        <Select placeholder="Select liquidity partner">
                          {FX_LIQUIDITY_PARTNER_OPTIONS.map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item
                        name="baseRateSource"
                        label="Base Rate Source"
                        rules={[{ required: true, message: 'Base rate source is required for FX' }]}
                      >
                        <Select placeholder="Select base rate source">
                          {FX_BASE_RATE_SOURCE_OPTIONS.map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item
                        name="valueDateConvention"
                        label="Value Date Convention"
                        rules={[{ required: true, message: 'Value date convention is required for FX' }]}
                        initialValue="T+0"
                      >
                        <Select disabled>
                          {FX_VALUE_DATE_CONVENTION_OPTIONS.map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item
                        name="bidSpreadBps"
                        label="Bid Spread (bps)"
                        rules={[{ required: true, message: 'Bid spread is required for FX' }]}
                      >
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item
                        name="askSpreadBps"
                        label="Ask Spread (bps)"
                        rules={[{ required: true, message: 'Ask spread is required for FX' }]}
                      >
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item
                        name="offmarketSpreadBps"
                        label="Off-Market Spread (bps) - Optional"
                      >
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item
                        name="weekendHolidaySpreadBps"
                        label="Weekend/Holiday Spread (bps) - Optional"
                      >
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Alert
                        message="Xendit buys base → ask; sells base → bid"
                        type="info"
                        showIcon
                        style={{ marginBottom: '8px' }}
                      />
                    </Col>
                  </>
                )}
              </Row>
            </Card>
          )}

          {/* Dynamic Pricing Configuration */}
          {selectedScheme && (
            <Card size="small" title={`Pricing Configuration - ${selectedScheme.scheme.toUpperCase()}`} style={{ marginBottom: '24px' }}>
              {renderPricingFields()}

              {/* Fee Preview */}
              <Card size="small" style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', marginTop: '16px' }}>
                <Row align="middle" gutter={16}>
                  <Col>
                    <CalculatorOutlined style={{ color: '#52c41a' }} />
                  </Col>
                  <Col flex={1}>
                    <Text strong>Fee Preview:</Text>
                    <div>
                      Amount: <InputNumber
                        value={previewAmount}
                        onChange={setPreviewAmount}
                        onBlur={calculatePreview}
                        style={{ width: '120px', margin: '0 8px' }}
                      />
                      → Fee: <Text strong style={{ color: '#52c41a' }}>
                        {feePreview !== null ? formatCurrency(feePreview, form.getFieldValue('feeCurrencyCode') || 'USD') : 'Calculate'}
                      </Text>
                    </div>
                  </Col>
                  <Col>
                    <Button size="small" onClick={calculatePreview}>
                      Calculate
                    </Button>
                  </Col>
                </Row>
              </Card>
            </Card>
          )}

          {/* Tiered Pricing (if applicable) */}
          {selectedScheme?.scheme === 'tier' && (
            <Card size="small" title="Tiered Pricing Configuration" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item name={['tieredPricing', 'tierAllOrStack']} label="Tier Type" rules={[{ required: true, message: 'Please select tier type' }]}>
                    <Select placeholder="Select tier type">
                      <Option value="All">All</Option>
                      <Option value="Stack">Stack</Option>
                      <Option value="Per Transaction">Per Transaction</Option>
                    </Select>
                  </Form.Item>
                </Col>
                
                <Col xs={24} sm={12}>
                  <Form.Item name={['tieredPricing', 'tierCountModel']} label="Count Model" rules={[{ required: true, message: 'Please select count model' }]}>
                    <Select placeholder="Select count model">
                      <Option value="Transaction Count">Transaction Count</Option>
                      <Option value="TPV">TPV</Option>
                    </Select>
                  </Form.Item>
                </Col>
                
                <Col xs={24} sm={8}>
                  <Form.Item name={['tieredPricing', 'tier1End']} label="Tier 1 End">
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="1000"
                      min={0}
                    />
                  </Form.Item>
                </Col>
                
                <Col xs={24} sm={8}>
                  <Form.Item name={['tieredPricing', 'tier1FixedFee']} label="Tier 1 Fixed Fee">
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="0.00"
                      min={0}
                      precision={4}
                    />
                  </Form.Item>
                </Col>
                
                <Col xs={24} sm={8}>
                  <Form.Item name={['tieredPricing', 'tier1MDR']} label="Tier 1 MDR (%)">
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="0.00"
                      min={0}
                      max={100}
                      precision={6}
                    />
                  </Form.Item>
                </Col>

                {/* Tier 2 */}
                <Col xs={24} sm={8}>
                  <Form.Item name={['tieredPricing', 'tier2End']} label="Tier 2 End">
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="5000"
                      min={0}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8}>
                  <Form.Item name={['tieredPricing', 'tier2FixedFee']} label="Tier 2 Fixed Fee">
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="0.00"
                      min={0}
                      precision={4}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8}>
                  <Form.Item name={['tieredPricing', 'tier2MDR']} label="Tier 2 MDR (%)">
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="0.00"
                      min={0}
                      max={100}
                      precision={6}
                    />
                  </Form.Item>
                </Col>

                {/* Tier 3 */}
                <Col xs={24} sm={8}>
                  <Form.Item name={['tieredPricing', 'tier3End']} label="Tier 3 End">
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="10000"
                      min={0}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8}>
                  <Form.Item name={['tieredPricing', 'tier3FixedFee']} label="Tier 3 Fixed Fee">
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="0.00"
                      min={0}
                      precision={4}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8}>
                  <Form.Item name={['tieredPricing', 'tier3MDR']} label="Tier 3 MDR (%)">
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="0.00"
                      min={0}
                      max={100}
                      precision={6}
                    />
                  </Form.Item>
                </Col>

                {/* Tier 4+ (unlimited) */}
                <Col xs={24} sm={12}>
                  <Form.Item name={['tieredPricing', 'tier4FixedFee']} label="Tier 4+ Fixed Fee">
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="0.00"
                      min={0}
                      precision={4}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item name={['tieredPricing', 'tier4MDR']} label="Tier 4+ MDR (%)">
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="0.00"
                      min={0}
                      max={100}
                      precision={6}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          )}

          {/* Additional Configuration */}
          <Card size="small" title="Additional Configuration" style={{ marginBottom: '24px' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="effectiveStartDate"
                  label="Effective Start Date"
                  rules={[{ required: true, message: 'Please select an effective start date' }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              

              
              {selectedProduct?.name === 'FOREX' ? (
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="includeVAT"
                    label="Include VAT"
                    rules={[{ required: true, message: 'Please select VAT option' }]}
                  >
                    <Select disabled>
                      <Select.Option value="NON_VAT">NON VAT</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              ) : (
                <>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="includeVAT"
                      label="Include VAT"
                      rules={[{ required: true, message: 'Please select VAT option' }]}
                    >
                      <Select placeholder="Select VAT option">
                        <Select.Option value="TRUE">TRUE</Select.Option>
                        <Select.Option value="FALSE">FALSE</Select.Option>
                        <Select.Option value="NON_VAT">NON VAT</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Form.Item noStyle dependencies={['includeVAT']}>
                    {() => form.getFieldValue('includeVAT') === 'TRUE' && (
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="vatRate"
                          label="VAT Rate (%)"
                          rules={[{ required: true, message: 'Please enter VAT rate' }]}
                        >
                          <InputNumber
                            min={0}
                            max={100}
                            step={0.01}
                            precision={2}
                            placeholder="e.g. 11 for 11%"
                            style={{ width: '100%' }}
                            addonAfter="%"
                          />
                        </Form.Item>
                      </Col>
                    )}
                  </Form.Item>
                </>
              )}

              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="costRefundedOnRefund"
                  label="Cost Refunded on Refund"
                  rules={[{ required: true, message: 'Please select refund policy' }]}
                >
                  <Select>
                    <Option value="TRUE">Full Refund</Option>
                    <Option value="FALSE">No Refund</Option>
                    <Option value="PARTIAL">Partial Refund</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="costRefundedOnVoid"
                  label="Cost Refunded on Void"
                  rules={[{ required: true, message: 'Please select void policy' }]}
                >
                  <Select>
                    <Option value="TRUE">Full Refund</Option>
                    <Option value="FALSE">No Refund</Option>
                    <Option value="PARTIAL">Partial Refund</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="costRefundedOnChargeback"
                  label="Cost Refunded on Chargeback"
                  rules={[{ required: true, message: 'Please select chargeback policy' }]}
                >
                  <Select>
                    <Option value="TRUE">Full Refund</Option>
                    <Option value="FALSE">No Refund</Option>
                    <Option value="PARTIAL">Partial Refund</Option>
                  </Select>
                </Form.Item>
              </Col>
              
              <Col xs={24}>
                <Form.Item
                  name="description"
                  label="Description"
                  rules={[{ required: true, message: "Description is required - attach partner's contract link and other details about the cost" }]}
                >
                  <TextArea
                    rows={3}
                    placeholder="Attach Partner's Contract Link and other details to the Description"
                    maxLength={1000}
                    showCount
                  />
                </Form.Item>
              </Col>
              
              <Col xs={24}>
                <Form.Item name="remarks" label="Remarks">
                  <TextArea
                    rows={2}
                    placeholder="Additional remarks..."
                    maxLength={1000}
                    showCount
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Form Actions */}
          <Card size="small">
            <Row justify="end">
              <Col>
                <Space>
                  <Button onClick={onCancel}>
                    Cancel
                  </Button>
                  
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={formLoading || loading}
                  >
                    {mode === 'create' ? 'Create Rule' :
                     mode === 'edit-as-new' ? 'Create New Version' :
                     'Save Changes'}
                  </Button>
                  
                  {mode === 'edit' && initialData?.status === 'DRAFT' && (
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={handleSubmitForApproval}
                      loading={formLoading || loading}
                      style={{ backgroundColor: '#722ed1' }}
                    >
                      Submit for Approval
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>
        </Form>
      </Card>
    </div>
  );
};

export default COGSRuleForm;
